/**
 * sf-sticky-buy — the bottom buy bar on product pages.
 *
 * Design notes
 * ------------
 * The bar does not reimplement add-to-cart. Its buttons forward the click to Dawn's own
 * submit button / dynamic checkout button, so the product form, variant input, quantity,
 * cart drawer, cart notification, error messages and quantity rules all behave exactly as
 * they do in the main buy box. That also means no duplicated AJAX or cart-rendering code.
 *
 * The section passes the whole variant list as JSON, so a variant change can refresh the
 * bar's price, title, image and availability deterministically, without depending on how
 * Dawn happens to re-render its price block.
 *
 * Lifetime: the theme editor re-renders a section by replacing its DOM, which upgrades a
 * fresh custom element. A module-scope `owner` handle makes sure only one instance arms
 * itself and that the outgoing one releases the handle on disconnect.
 */
(function () {
  var owner = null;

  class SfStickyBuy extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sfArmed === 'true') return;
      if (owner && owner !== this && owner.isConnected) {
        this.remove();
        return;
      }

      var root = document.querySelector('product-info[id^="MainProduct-"]');
      var form = root && root.querySelector('form[id^="product-form-"]');
      var mainButton = form && form.querySelector('button[name="add"]');
      if (!root || !form || !mainButton) {
        this.remove();
        return;
      }

      owner = this;
      this.dataset.sfArmed = 'true';
      this.root = root;
      this.form = form;
      this.mainButton = mainButton;
      this.addButton = this.querySelector('[data-sf-sticky-add]');
      this.buyNowButton = this.querySelector('[data-sf-sticky-buy-now]');
      this.image = this.querySelector('[data-sf-sticky-image]');
      this.variantLabel = this.querySelector('[data-sf-sticky-variant]');
      this.priceLabel = this.querySelector('[data-sf-sticky-price]');
      this.compareLabel = this.querySelector('[data-sf-sticky-compare]');
      this.paymentButton = form.querySelector('.shopify-payment-button button');

      try {
        this.variants = JSON.parse(this.dataset.sfVariants || '[]');
      } catch (error) {
        this.variants = [];
      }
      this.currentVariant = this.variants.length
        ? this.variants.find(function (variant) {
            return String(variant.id) === String(form.querySelector('input[name="id"]')?.value);
          }) || this.variants[0]
        : null;

      this.classList.add('is-armed');
      this.sync();

      this.onFormChange = this.sync.bind(this);
      form.addEventListener('change', this.onFormChange);

      this.onButtonMutation = this.onButtonMutation.bind(this);
      this.buttonObserver = new MutationObserver(this.onButtonMutation);
      this.buttonObserver.observe(mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });

      this.onAddClick = this.onAddClick.bind(this);
      this.addButton.addEventListener('click', this.onAddClick);
      if (this.buyNowButton) this.buyNowButton.addEventListener('click', this.onBuyNowClick.bind(this));

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0 });
      this.observer.observe(mainButton);

      /* In the theme editor nothing is scrolled, so the bar would never appear and could not
         be clicked to open its settings — keep it visible while editing. */
      this.editing = !!(window.Shopify && window.Shopify.designMode);
      if (this.editing) {
        this.classList.add('is-visible', 'is-editing');
        this.removeAttribute('inert');
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.buttonObserver) this.buttonObserver.disconnect();
      if (this.form && this.onFormChange) this.form.removeEventListener('change', this.onFormChange);
      if (this.addButton && this.onAddClick) this.addButton.removeEventListener('click', this.onAddClick);
      if (owner === this) owner = null;
      delete this.dataset.sfArmed;
    }

    /* --- reveal / hide ------------------------------------------------- */

    onIntersect(entries) {
      if (this.editing) return;
      var entry = entries[entries.length - 1];
      // only after the real buy button has left the viewport *upwards*
      var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
    }

    /* --- state --------------------------------------------------------- */

    sync() {
      var idInput = this.form.querySelector('input[name="id"]');
      var variant = this.variants.find(function (item) {
        return String(item.id) === String(idInput && idInput.value);
      });
      if (variant) this.currentVariant = variant;
      var current = this.currentVariant;

      if (current) {
        if (this.variantLabel) this.variantLabel.textContent = current.title;
        if (this.priceLabel) this.priceLabel.textContent = current.price;
        if (this.compareLabel) {
          this.compareLabel.textContent = current.compare || '';
          this.compareLabel.hidden = !current.compare;
        }
        if (this.image && current.image) {
          this.image.src = current.image;
          this.image.removeAttribute('srcset');
        }
      }

      this.currentAvailable = current
        ? current.available === true
        : !this.mainButton.disabled && this.mainButton.getAttribute('aria-disabled') !== 'true';

      if (this.buyNowButton) this.buyNowButton.hidden = !this.paymentButton || !this.currentAvailable;
      this.mirrorButtonState();
    }

    onButtonMutation() {
      this.mirrorButtonState();
    }

    mirrorButtonState() {
      var busy =
        this.mainButton.getAttribute('aria-disabled') === 'true' || this.mainButton.disabled === true;
      var label = !this.currentAvailable
        ? this.dataset.sfLabelSoldOut
        : busy
          ? this.dataset.sfLabelAdding
          : this.dataset.sfLabel;
      if (this.addButton) {
        this.addButton.textContent = label;
        this.addButton.disabled = !this.currentAvailable;
        this.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
    }

    /* --- actions ------------------------------------------------------- */

    onAddClick() {
      if (!this.currentAvailable) return;
      this.mainButton.click();
    }

    onBuyNowClick() {
      if (!this.paymentButton) return;
      this.buyNowButton.hidden = true; // let the real checkout button take over
      this.paymentButton.click();
    }
  }

  if (!customElements.get('sf-sticky-buy')) {
    customElements.define('sf-sticky-buy', SfStickyBuy);
  }
})();
